<template>
  <article
    class="changelog-entry-card"
    :style="`--i: ${cappedIndex}`"
  >
    <div
      v-if="imageUrl && !imageError"
      class="changelog-entry-card__illustration"
    >
      <img
        :src="imageUrl"
        :alt="imageAlt ?? ''"
        :class="{ 'changelog-entry-card__image--loaded': imageLoaded }"
        @error="handleImageError"
        @load="onImageLoad"
      />
    </div>

    <header class="changelog-entry-card__head">
      <span class="changelog-entry-card__version font-mono">v{{ version }}</span>

      <KdsBadge :tone="typeTone">{{ t(`changelogs.types.${type}`) }}</KdsBadge>
      <KdsBadge :tone="categoryTone">{{ t(`changelogs.categories.${category}`) }}</KdsBadge>

      <time class="changelog-entry-card__date" :datetime="date">{{ formattedDate }}</time>
    </header>

    <h2 class="changelog-entry-card__title">{{ title }}</h2>
    <p v-if="summary" class="changelog-entry-card__summary">{{ summary }}</p>
    <ul v-if="highlights && highlights.length > 0" class="changelog-entry-card__highlights">
      <li v-for="(item, i) in highlights" :key="i" class="changelog-entry-card__highlight">
        {{ item }}
      </li>
    </ul>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { KdsBadge } from '@/kds';
import type { KdsBadgeTone } from '@/kds/types';
import type { EntryType, EntryCategory } from '@/types/changelog';

interface Props {
  version: string;
  type: EntryType;
  category: EntryCategory;
  date: string;
  title: string;
  summary?: string;
  highlights?: string[];
  imageUrl?: string;
  imageAlt?: string;
  locale?: string;
  index?: number;
}

const props = withDefaults(defineProps<Props>(), {
  summary: undefined,
  highlights: undefined,
  imageUrl: undefined,
  imageAlt: undefined,
  locale: 'en',
  index: 0
});

const imageError = ref(false);
const imageLoaded = ref(false);

const handleImageError = (): void => {
  imageError.value = true;
};

const onImageLoad = (): void => {
  imageLoaded.value = true;
};

const { t } = useI18n();

// Cap stagger at 8 items to avoid excessive delay on long lists.
const cappedIndex = computed<number>(() => Math.min(props.index, 8));

const typeTone = computed<KdsBadgeTone>(() => {
  const map: Record<EntryType, KdsBadgeTone> = {
    major: 'danger',
    minor: 'ok',
    patch: 'muted'
  };
  return map[props.type];
});

const categoryTone = computed<KdsBadgeTone>(() => {
  const map: Record<EntryCategory, KdsBadgeTone> = {
    feature: 'info',
    fix: 'muted'
  };
  return map[props.category];
});

const formattedDate = computed<string>(() => {
  try {
    const d = new Date(props.date);
    if (isNaN(d.getTime())) return props.date;
    return d.toLocaleDateString(props.locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return props.date;
  }
});
</script>

<style scoped lang="scss">
.changelog-entry-card {
  // Overflow hidden required to clip illustration into card border-radius (R1)
  overflow: hidden;
  padding: var(--space-5);
  border-radius: var(--r-4);
  border: var(--bw) solid var(--mist);
  background: var(--paper);
  box-shadow: var(--shadow-1);
  transition:
    border-color var(--d-fast) var(--ease-out),
    box-shadow var(--d-fast) var(--ease-out);

  &:hover {
    border-color: var(--mist-2);
    box-shadow: var(--shadow-2);
  }

  &__illustration {
    aspect-ratio: 3 / 1;
    overflow: hidden;
    border-radius: var(--r-4) var(--r-4) 0 0;
    margin: calc(var(--space-5) * -1) calc(var(--space-5) * -1) var(--space-4);
    background: var(--paper-2);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      opacity: 0;
      transition: opacity 120ms var(--ease-out);

      &.changelog-entry-card__image--loaded {
        opacity: 1;
      }
    }
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }

  &__version {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    font-weight: var(--fw-semibold);
    color: var(--ink);
  }

  &__date {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone);
    margin-left: auto;
  }

  &__title {
    margin: 0 0 var(--space-2);
    font-size: var(--fs-h2);
    font-weight: var(--fw-semibold);
    line-height: var(--lh-h2);
    letter-spacing: var(--ls-h2);
    color: var(--ink);
  }

  &__summary {
    margin: 0 0 var(--space-3);
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    color: var(--stone);
  }

  &__highlights {
    margin: 0;
    padding-left: var(--space-5);
  }

  &__highlight {
    font-size: var(--fs-body);
    line-height: var(--lh-body);
    color: var(--stone);
    margin: var(--space-1) 0;
  }
}

@media (max-width: 600px) {
  .changelog-entry-card {
    &__head {
      gap: var(--space-2);
    }

    &__date {
      margin-left: 0;
      width: 100%;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .changelog-entry-card {
    transition: none;

    &__illustration img {
      transition: none;
    }
  }
}
</style>
